import type { z } from 'zod';
import { forwardRef, useMemo, useRef, useState } from 'react';

import type { ReportSchema } from '@ballerine/common';
import type { CustomerFeatures } from '@ballerine/customer-schemas';

import { useRegisterTranslationProtectedTerms } from '@/common/translation/translation-protection-store';
import { ctw } from '@/common/utils/ctw';
import { SectionObserver } from '../../../composites';
import { ReportComplianceFooter } from '../../../report-shared/components/ReportComplianceFooter';
import { useDigitalFootprintReportSections } from '../../hooks/useDigitalFootprintReportSections/useDigitalFootprintReportSections';
import { GatedContentCheckBar } from '../GatedContentCheckBar';

type DigitalFootprintReportProps = {
  report: z.infer<typeof ReportSchema>;
  customerFeatures?: CustomerFeatures | null;
  disableSectionObserver?: boolean;
  FeedbackComponent?: React.ComponentType<{ reportId: string }>;
  locale?: string;
  googleMapsApiKey: string;
  onOpenOriginalPolicy?: () => void;
  BusinessDetailsSlot?: React.ComponentType;
};

export const DigitalFootprintReport = forwardRef<HTMLDivElement, DigitalFootprintReportProps>(
  (
    {
      report,
      customerFeatures,
      disableSectionObserver = false,
      FeedbackComponent,
      locale,
      googleMapsApiKey,
      onOpenOriginalPolicy,
      BusinessDetailsSlot,
    },
    ref,
  ) => {
    const { sections } = useDigitalFootprintReportSections(
      report,
      customerFeatures,
      googleMapsApiKey,
      FeedbackComponent,
      locale,
      onOpenOriginalPolicy,
      BusinessDetailsSlot,
    );
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const translationProtectedTerms = useMemo(() => {
      const terms = new Set<string>();

      const addTerm = (value?: string | null) => {
        if (value?.trim()) {
          terms.add(value.trim());
        }
      };

      addTerm(report.data?.companyName as string | undefined);
      addTerm(report.data?.merchantDetails?.companyName as string | undefined);
      addTerm(report.customer.displayName as string | undefined);
      addTerm(report.website?.url as string | undefined);

      return Array.from(terms);
    }, [report]);

    const isTranslationFeatureEnabled = Boolean(customerFeatures?.enableTranslation);
    useRegisterTranslationProtectedTerms(
      isTranslationFeatureEnabled ? translationProtectedTerms : [],
    );
    const isGatedContent = useMemo(
      () => Boolean(report?.data?.gatedContentCheck),
      [report?.data?.gatedContentCheck],
    );
    // Use only videoUrl - no fallback to sessionUrl
    const gatedContentVideoUrl = report.data?.gatedContentCheck?.videoUrl ?? null;

    return (
      <div
        className={ctw(
          'mx-auto flex justify-center gap-x-8 ps-2 transition-all duration-300',
          isSidebarOpen ? '2xl:max-w-8xl' : '2xl:max-w-9xl',
        )}
      >
        <div className="max-w-[62.5rem] overflow-y-visible transition-all duration-300" ref={ref}>
          {isGatedContent && (
            <GatedContentCheckBar
              status={(report.data?.gatedContentCheck?.status as 'success' | 'failed') ?? 'failed'}
              message={report.data?.gatedContentCheck?.message ?? undefined}
              videoUrl={gatedContentVideoUrl}
              screenshotUrl={report.data?.gatedContentCheck?.screenshotUrl ?? null}
              websiteUrl={report.websiteUrl as string | undefined}
              isGatedContentCheckEnabled={
                customerFeatures?.DIGITAL_FOOTPRINT?.enableGatedContentCheck ?? false
              }
            />
          )}
          {sections.map(section => {
            return (
              <div
                key={section.id}
                id={section.id}
                ref={el => (sectionRefs.current[section.id] = el)}
                className="min-h-[100px]"
              >
                {section.Component}
              </div>
            );
          })}
          <ReportComplianceFooter />
        </div>

        {!disableSectionObserver && (
          <SectionObserver
            sections={sections}
            sectionRefs={sectionRefs}
            isSidebarOpen={isSidebarOpen}
            onSidebarToggle={setIsSidebarOpen}
          />
        )}
      </div>
    );
  },
);

DigitalFootprintReport.displayName = 'DigitalFootprintReport';
